import { z } from 'zod';
import { macroSetSchema } from './food.js';
import { trialValueKindSchema } from './monetization.js';
import { weeklyWinSchema } from './progress.js';
/* ==========================================================================
 *  Summary document contract (ISSUE-030 / ISSUE-031 / ISSUE-032)
 *
 *  FR-6.4: summaries are STRUCTURED DOCUMENTS, not free text. This is the shape
 *  stored in the `summary` table and later composed into chat context (FR-8.1).
 *
 *  The governing invariant mirrors FR-5.4 (macro arithmetic excluded from AI):
 *  **every NUMBER in a summary is computed deterministically from the ledger;
 *  the LLM only writes the NARRATIVE around those numbers and must CITE them.**
 *  A model never emits an adherence %, a trend value, or a macro total — it is
 *  handed the computed `metrics` block and asked to narrate it. The narrative
 *  carries citation ids that point back to the metrics/source entries (J4: "the
 *  narrative is generated but cites structured numbers from the ledger").
 *
 *  CONTRACT TYPES only — zod is the single source of truth; the web app renders
 *  these (weekly review, daily card) and chat composes them, so nothing here may
 *  import a provider SDK or anything server-only. The jobs that PRODUCE these
 *  live in the server-only summary worker (see ADR-0004).
 * ========================================================================== */
/* ----- Period identity --------------------------------------------------- */
/** Which rollup. `day` = ISSUE-031, `week` = ISSUE-030 (Monday cron),
 *  `month` = ISSUE-032 (aggregation of weeklies, no new LLM call). */
export const summaryPeriodSchema = z.enum(['day', 'week', 'month']);
/* ----- Deterministic metrics block (computed, never AI) ------------------ */
/** Adherence for one macro over the period: how the deterministic ledger sum
 *  compares to the (period-summed) target. `pct` is consumed/target (1.0 = on
 *  target), null when there was no target. `delta` = consumed − target (signed;
 *  negative = under). All computed in pure code from the ledger. */
export const macroAdherenceSchema = z.object({
    consumed: z.number().nonnegative(),
    target: z.number().nonnegative().nullable(),
    pct: z.number().nonnegative().nullable(), // consumed / target
    delta: z.number().nullable(), // consumed − target
});
/** Direction of a trend vs the prior comparable period. */
export const trendDirectionSchema = z.enum(['up', 'down', 'flat']);
/** A trend for one metric vs the previous period (prior day/week/month).
 *  `changePct` is signed ((current − prior) / prior); null when no prior data. */
export const metricTrendSchema = z.object({
    metric: z.string(), // "kcal" | "proteinG" | ...
    current: z.number(),
    previous: z.number().nullable(),
    changePct: z.number().nullable(),
    direction: trendDirectionSchema,
});
/**
 * The deterministic core of a summary. Computed from the ledger
 * (`computeDaySummary`/`computeDaysRange` and friends) — NO AI. The narrative
 * is generated *from* this block; it is the citable source of truth.
 */
export const summaryMetricsSchema = z.object({
    /** Period totals (absolute macros consumed). */
    consumed: macroSetSchema,
    /** Daily averages across the period (reuses the ledger range math). */
    dailyAverage: macroSetSchema,
    /** Per-macro adherence vs target. Keys are the five macro names. */
    adherence: z.object({
        kcal: macroAdherenceSchema,
        proteinG: macroAdherenceSchema,
        carbsG: macroAdherenceSchema,
        fatG: macroAdherenceSchema,
        fiberG: macroAdherenceSchema,
    }),
    /** Overall adherence score 0–1 (e.g. mean of clamped per-macro pct). */
    adherenceScore: z.number().min(0).max(1).nullable(),
    /** Trends vs the prior comparable period. */
    trends: z.array(metricTrendSchema).default([]),
    /** How many days in the period had at least one logged entry (engagement). */
    daysLogged: z.number().int().nonnegative(),
    /** Total days in the period (7 for week; days-in-month; 1 for day). */
    daysInPeriod: z.number().int().positive(),
});
/* ----- Citations (narrative → numbers) ----------------------------------- */
/** What a narrative sentence points at, so a numeric claim is always traceable
 *  (J4 / FR-6.2 "numeric claims cite source entries"; NFR audit). */
export const citationTargetSchema = z.enum(['metric', 'entry', 'day']);
/** One citation: `ref` is a metric key ("adherence.proteinG"), a `meal_entry`
 *  id, or an ISO date, depending on `target`. */
export const summaryCitationSchema = z.object({
    id: z.string(), // citation marker referenced from narrative text, e.g. "c1"
    target: citationTargetSchema,
    ref: z.string(),
});
/* ----- LLM-authored narrative block (words only, no new numbers) --------- */
/**
 * The generated narrative. The model fills these fields given `metrics`; any
 * number it writes must already exist in `metrics` and be cited. Strings stay
 * short (one-screen, 30-second scan per J4). `headline` is the single-insight
 * surface; `weakSpot`/`win` are the J4 one-weak-spot / one-win.
 */
export const summaryNarrativeSchema = z.object({
    headline: z.string().max(280), // "Solid week — protein was the gap."
    weakSpot: z.string().max(280).nullable().default(null),
    win: z.string().max(280).nullable().default(null),
    body: z.string().max(2000).nullable().default(null), // optional longer prose
    /** Citations referenced by `{{cN}}` markers in the strings above. */
    citations: z.array(summaryCitationSchema).default([]),
});
/* ----- Suggested target adjustment (proposal only, never auto-applied) --- */
/**
 * An optional AI-suggested target change (FR-6.2 / J4 step 3). It is a PROPOSAL:
 * never auto-applied. Accepting it inserts a versioned `target` row (ISSUE-013)
 * via the explicit user action (05-ux-standards: "no auto-apply"). The kcal
 * value is bounded by the ED-safety floor/cap (ISSUE-018/058) at suggestion time
 * AND re-validated on accept — `cappedBySafety` records whether it was clamped.
 */
export const suggestedTargetAdjustmentSchema = z.object({
    kcal: z.number().int().positive(),
    proteinG: z.number().int().nonnegative(),
    carbsG: z.number().int().nonnegative(),
    fatG: z.number().int().nonnegative(),
    fiberG: z.number().int().nonnegative(),
    rationale: z.string().max(500),
    cappedBySafety: z.boolean().default(false),
});
/* ----- The summary document --------------------------------------------- */
/** Generation provenance for a summary. `metricsOnly` summaries (e.g. a monthly
 *  rollup that reused weekly narratives, or a fallback when the LLM call failed)
 *  have `narrative` present but `model`/`promptId` null. */
export const summaryGenerationSchema = z.object({
    generatedAt: z.string().datetime(),
    model: z.string().nullable().default(null), // null = no LLM call (pure aggregation)
    promptId: z.string().nullable().default(null),
    promptVersion: z.string().nullable().default(null),
});
const premiumAdaptiveTargetFactSchema = z.object({
    type: z.literal('adaptive_target_applied'),
    kind: z.literal(trialValueKindSchema.enum.adaptive_coaching),
    direction: z.enum(['increased', 'decreased', 'maintained', 'set']),
    magnitudeKcal: z.number().int().nonnegative().nullable(),
    maintenanceSafe: z.boolean(),
});
const premiumPlanningFactSchema = z.object({
    type: z.literal('planning_actions'),
    kind: z.literal(trialValueKindSchema.enum.planning),
    templateDays: z.number().int().nonnegative(),
    buildRestDays: z.number().int().nonnegative(),
    totalDays: z.number().int().positive(),
});
const premiumReadinessFactSchema = z.object({
    type: z.literal('readiness_maintained'),
    kind: z.literal(trialValueKindSchema.enum.adaptive_coaching),
    maintenanceSafe: z.boolean(),
});
export const premiumValueReceiptFactSchema = z.discriminatedUnion('type', [
    premiumAdaptiveTargetFactSchema,
    premiumPlanningFactSchema,
    premiumReadinessFactSchema,
]);
export const premiumValueReceiptSchema = z.object({
    facts: z.array(premiumValueReceiptFactSchema).max(2),
    pointer: z.enum(['coaching_review', 'planning']).nullable(),
});
/**
 * The full structured summary document (FR-6.4). Stored as the `payload` JSONB
 * of the `summary` table, keyed by (userId, period, periodStart). `metrics` is
 * the deterministic, citable core; `narrative` is the generated wrapper;
 * `suggestedTargetAdjustment` is an optional, never-auto-applied proposal.
 */
export const summaryDocumentSchema = z.object({
    userId: z.string(),
    period: summaryPeriodSchema,
    /** ISO date of the first day of the period (the table's unique key with
     *  user+period). For `week` this is the Monday; for `month`, the 1st. */
    periodStart: z.string().date(),
    periodEnd: z.string().date(),
    metrics: summaryMetricsSchema,
    narrative: summaryNarrativeSchema,
    suggestedTargetAdjustment: suggestedTargetAdjustmentSchema.nullable().default(null),
    generation: summaryGenerationSchema,
    // CONTRACT TOUCH (ISSUE-067): weekly win chips, ≤3 per week, computed in the
    // weekly-summary job. Absent when there are zero wins or the progress layer
    // is disabled for this user. Clients must treat absence as "no chips to show".
    weeklyWins: z.array(weeklyWinSchema).max(3).optional(),
    /** T49.1 deterministic proof-of-work block. Present only on Premium weeklies. */
    premiumValueReceipt: premiumValueReceiptSchema.optional(),
});
/* ----- HTTP views (ISSUE-030/031/032 read endpoints) --------------------- */
/** `GET /me/summaries/{day|week|month}/:period` returns the stored document, or
 *  202 + this envelope while generation is in flight (ISSUE-031 on-demand). */
export const summaryResponseSchema = z.object({
    status: z.enum(['ready', 'generating']),
    summary: summaryDocumentSchema.nullable().default(null),
});
/* ----- Active alerts (FR-7.2 / ISSUE-033) — composed into chat context --- */
/** An active goal-recovery alert. Surfaced standalone (push/email, ISSUE-033)
 *  AND folded into chat context (FR-8.1 "+ active alerts"). Defined here, beside
 *  summaries, because both are the "what the assistant knows" context layer. */
export const activeAlertSchema = z.object({
    id: z.string(),
    kind: z.enum(['goal_recovery', 'streak', 'anomaly']),
    macro: z.string().nullable().default(null), // which macro the gap is about
    message: z.string().max(280),
    createdAt: z.string().datetime(),
});
//# sourceMappingURL=summary.js.map